<template>
  <div class="customer-account">
    <LoadingSpinner v-if="loading && !loaded" />

    <template v-else>
      <!-- Customer-wide contacts -->
      <section class="customer-account__section">
        <header class="customer-account__header">
          <div>
            <h3 class="customer-account__title">
              {{ $t('customer_accounts.contacts.title') }}
            </h3>
            <p class="customer-account__subtitle">
              {{ $t('customer_accounts.contacts.subtitle') }}
            </p>
          </div>
          <Btn
            :text="$t('customer_accounts.contacts.add')"
            icon="plus"
            variant="secondary"
            size="sm"
            @click="openContactModal()"
          />
        </header>

        <div v-if="customerContacts.length" class="customer-account__contacts">
          <div
            v-for="contact in customerContacts"
            :key="contact.id"
            class="customer-account__contact"
            :class="{ 'customer-account__contact--archived': contact.archived_at }"
          >
            <div class="customer-account__contact-main">
              <div class="customer-account__contact-name">
                <BaseIcon v-if="contact.is_primary" name="star-fill" size="12" />
                {{ contactName(contact) }}
                <span v-if="contact.job_title" class="customer-account__muted">
                  · {{ contact.job_title }}
                </span>
              </div>
              <div class="customer-account__contact-meta">
                <a v-if="contact.email" :href="`mailto:${contact.email}`">{{ contact.email }}</a>
                <a v-if="contact.phone" :href="`tel:${contact.phone}`">{{ contact.phone }}</a>
              </div>
            </div>
            <div class="customer-account__roles">
              <span v-for="role in contact.roles || []" :key="role" class="customer-account__role">
                {{ $t(`customer_accounts.contacts.roles.${role}`) }}
              </span>
            </div>
            <div class="customer-account__row-actions">
              <template v-if="contact.archived_at">
                <Btn
                  :text="$t('customer_accounts.restore')"
                  variant="plain"
                  size="xs"
                  @click="restoreContact(contact)"
                />
              </template>
              <template v-else>
                <Btn icon="edit" variant="plain" size="xs" @click="openContactModal(contact)" />
                <Btn icon="trash" variant="plain" size="xs" @click="archiveContact(contact)" />
              </template>
            </div>
          </div>
        </div>
        <p v-else class="customer-account__empty">
          {{ $t('customer_accounts.contacts.empty', { name: leadName }) }}
        </p>
      </section>

      <!-- Sites -->
      <section class="customer-account__section">
        <header class="customer-account__header">
          <div>
            <h3 class="customer-account__title">
              {{ $t('customer_accounts.sites.title') }}
              <span v-if="activeSites.length" class="customer-account__count">
                {{ activeSites.length }}
              </span>
            </h3>
            <p class="customer-account__subtitle">
              {{ $t('customer_accounts.sites.subtitle') }}
            </p>
          </div>
          <div class="customer-account__header-actions">
            <Btn
              :text="$t('customer_accounts.sites.bulk.open')"
              icon="clipboard"
              variant="outlined"
              size="sm"
              @click="openBulkModal"
            />
            <Btn
              :text="$t('customer_accounts.sites.add')"
              icon="plus"
              variant="secondary"
              size="sm"
              @click="openSiteModal()"
            />
          </div>
        </header>

        <div v-if="sites.length" class="customer-account__toolbar">
          <input
            v-model="search"
            type="search"
            class="customer-account__search"
            :placeholder="$t('customer_accounts.sites.search')"
          />
          <label class="customer-account__toggle">
            <input type="checkbox" v-model="showArchived" />
            {{ $t('customer_accounts.show_archived') }}
          </label>
        </div>

        <EmptyState
          v-if="!sites.length"
          icon="location"
          :title="$t('customer_accounts.sites.empty_title')"
          :description="$t('customer_accounts.sites.empty_description')"
        />

        <p v-else-if="!filteredSites.length" class="customer-account__empty">
          {{ $t('customer_accounts.sites.no_results') }}
        </p>

        <div v-else class="customer-account__sites">
          <article
            v-for="site in filteredSites"
            :key="site.id"
            class="customer-account__site"
            :class="{ 'customer-account__site--archived': site.archived_at }"
          >
            <div class="customer-account__site-head">
              <BaseIcon name="location" size="16" class="customer-account__site-icon" />
              <div class="customer-account__site-info">
                <div class="customer-account__site-title">
                  {{ siteTitle(site) }}
                  <span v-if="site.external_ref" class="customer-account__ref">
                    {{ site.external_ref }}
                  </span>
                  <span v-if="site.archived_at" class="customer-account__ref">
                    {{ $t('customer_accounts.archived') }}
                  </span>
                </div>
                <div v-if="site.label" class="customer-account__muted">
                  {{ formatSiteAddress(site.address) }}
                </div>
                <div class="customer-account__usage">
                  <span>
                    {{
                      $t('customer_accounts.sites.usage.proposals', {
                        count: usage(site).proposals,
                      })
                    }}
                  </span>
                  <span>
                    {{
                      $t('customer_accounts.sites.usage.projects', { count: usage(site).projects })
                    }}
                  </span>
                  <span v-if="usage(site).open_work_orders" class="customer-account__usage--open">
                    {{
                      $t('customer_accounts.sites.usage.open_work_orders', {
                        count: usage(site).open_work_orders,
                      })
                    }}
                  </span>
                </div>
              </div>
              <div class="customer-account__row-actions">
                <template v-if="site.archived_at">
                  <Btn
                    :text="$t('customer_accounts.restore')"
                    variant="plain"
                    size="xs"
                    @click="restoreSite(site)"
                  />
                </template>
                <template v-else>
                  <Btn icon="edit" variant="plain" size="xs" @click="openSiteModal(site)" />
                  <Btn icon="trash" variant="plain" size="xs" @click="archiveSite(site)" />
                </template>
              </div>
            </div>

            <p v-if="site.access_notes" class="customer-account__access">
              <BaseIcon name="info" size="12" />
              {{ site.access_notes }}
            </p>

            <div class="customer-account__site-contacts">
              <div
                v-for="contact in contactsBySite[site.id] || []"
                :key="contact.id"
                class="customer-account__site-contact"
              >
                <BaseIcon name="user" size="12" />
                <span class="customer-account__site-contact-name">
                  {{ contactName(contact) }}
                  <span v-if="contact.job_title" class="customer-account__muted">
                    · {{ contact.job_title }}
                  </span>
                </span>
                <span class="customer-account__muted">
                  {{ [contact.phone, contact.email].filter(Boolean).join(' · ') }}
                </span>
                <span
                  v-for="role in contact.roles || []"
                  :key="role"
                  class="customer-account__role"
                >
                  {{ $t(`customer_accounts.contacts.roles.${role}`) }}
                </span>
                <div class="customer-account__row-actions">
                  <Btn icon="edit" variant="plain" size="xs" @click="openContactModal(contact)" />
                  <Btn icon="trash" variant="plain" size="xs" @click="archiveContact(contact)" />
                </div>
              </div>
              <button
                v-if="!site.archived_at"
                type="button"
                class="customer-account__add-inline"
                @click="openContactModal(null, site.id)"
              >
                + {{ $t('customer_accounts.sites.add_contact') }}
              </button>
            </div>
          </article>
        </div>
      </section>
    </template>
  </div>
</template>

<script>
  import { defineComponent, ref, computed, onMounted, watch, getCurrentInstance } from 'vue';
  import { customerAccountService } from '@/services/customerAccountService';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';
  import { useCustomerAccount } from '@/composables/useCustomerAccount';
  import Btn from '@/components/partials/Btn.vue';
  import EmptyState from '@/components/partials/EmptyState.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import { contactName, formatSiteAddress, siteTitle } from '@shared/utils/customerAccounts';
  import CustomerSiteModal from './CustomerSiteModal.vue';
  import CustomerContactModal from './CustomerContactModal.vue';
  import BulkAddSitesModal from './BulkAddSitesModal.vue';

  const EMPTY_USAGE = { proposals: 0, projects: 0, work_orders: 0, open_work_orders: 0 };

  export default defineComponent({
    name: 'CustomerAccountPanel',
    components: { Btn, EmptyState, LoadingSpinner, BaseIcon },
    props: {
      leadId: { type: String, required: true },
      leadName: { type: String, default: '' },
    },
    emits: ['changed'],
    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const leadIdRef = computed(() => props.leadId);
      const { sites, contacts, activeSites, loading, loaded, fetchAccount } =
        useCustomerAccount(leadIdRef);

      const search = ref('');
      const showArchived = ref(false);

      const reload = async () => {
        try {
          await fetchAccount({ includeArchived: showArchived.value });
        } catch (error) {
          toastStore.addToast({ type: 'error', message: $t('customer_accounts.load_failed') });
        }
      };

      onMounted(reload);
      watch(showArchived, reload);
      watch(() => props.leadId, reload);

      const visibleContacts = computed(() =>
        contacts.value.filter((contact) => showArchived.value || !contact.archived_at)
      );

      const customerContacts = computed(() =>
        visibleContacts.value.filter((contact) => !contact.site_id)
      );

      const contactsBySite = computed(() =>
        visibleContacts.value.reduce((acc, contact) => {
          if (!contact.site_id) return acc;
          (acc[contact.site_id] ||= []).push(contact);
          return acc;
        }, {})
      );

      const filteredSites = computed(() => {
        const term = search.value.trim().toLowerCase();
        return sites.value
          .filter((site) => showArchived.value || !site.archived_at)
          .filter((site) => {
            if (!term) return true;
            const residents = (contactsBySite.value[site.id] || []).map(contactName).join(' ');
            const haystack = [
              site.label,
              site.external_ref,
              formatSiteAddress(site.address),
              residents,
            ]
              .filter(Boolean)
              .join(' ')
              .toLowerCase();
            return haystack.includes(term);
          });
      });

      const usage = (site) => site.usage || EMPTY_USAGE;

      const errorMessage = (error, fallbackKey) => error.response?.data?.error || $t(fallbackKey);

      const afterChange = async (messageKey, params) => {
        toastStore.addToast({ type: 'success', message: $t(messageKey, params) });
        await reload();
        emit('changed');
      };

      const openSiteModal = (site = null) => {
        const modalId = uiStore.addModal(CustomerSiteModal, {
          title: site ? $t('customer_accounts.sites.edit') : $t('customer_accounts.sites.add'),
          size: 'lg',
          site,
          onCancel: () => uiStore.closeModal(modalId),
          onSubmit: async (payload) => {
            try {
              if (site) {
                const updated = await customerAccountService.updateSite(
                  props.leadId,
                  site.id,
                  payload
                );
                uiStore.closeModal(modalId);
                const syncedCount =
                  (updated.synced?.projects || 0) + (updated.synced?.work_orders || 0);
                await afterChange(
                  syncedCount
                    ? 'customer_accounts.sites.updated_synced'
                    : 'customer_accounts.sites.updated',
                  { count: syncedCount }
                );
              } else {
                await customerAccountService.createSite(props.leadId, payload);
                uiStore.closeModal(modalId);
                await afterChange('customer_accounts.sites.created');
              }
            } catch (error) {
              toastStore.addToast({
                type: 'error',
                message: errorMessage(error, 'customer_accounts.save_failed'),
              });
            }
          },
        });
      };

      const openBulkModal = () => {
        let createdTotal = 0;
        const modalId = uiStore.addModal(BulkAddSitesModal, {
          title: $t('customer_accounts.sites.bulk.title'),
          size: 'lg',
          onCancel: () => uiStore.closeModal(modalId),
          onSubmit: async (lines) => {
            try {
              const result = await customerAccountService.bulkCreateSites(props.leadId, lines);
              createdTotal += result.created?.length || 0;
              if (!result.failed?.length) uiStore.closeModal(modalId);
              if (result.created?.length) {
                await afterChange('customer_accounts.sites.bulk.created', {
                  count: createdTotal,
                });
              }
              return result;
            } catch (error) {
              toastStore.addToast({
                type: 'error',
                message: errorMessage(error, 'customer_accounts.save_failed'),
              });
              return null;
            }
          },
        });
      };

      const archiveSite = (site) => {
        const open = usage(site).open_work_orders;
        const modalId = uiStore.addModal(false, {
          title: $t('customer_accounts.sites.archive_title', { name: siteTitle(site) }),
          description: open
            ? $t('customer_accounts.sites.archive_open_warning', { count: open })
            : $t('customer_accounts.sites.archive_description'),
          submitText: $t('customer_accounts.archive'),
          cancelText: $t('common.cancel'),
          onCancel: () => uiStore.closeModal(modalId),
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            try {
              await customerAccountService.archiveSite(props.leadId, site.id);
              await afterChange('customer_accounts.sites.archived');
            } catch (error) {
              toastStore.addToast({
                type: 'error',
                message: errorMessage(error, 'customer_accounts.save_failed'),
              });
            }
          },
        });
      };

      const restoreSite = async (site) => {
        try {
          await customerAccountService.restoreSite(props.leadId, site.id);
          await afterChange('customer_accounts.sites.restored');
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: errorMessage(error, 'customer_accounts.save_failed'),
          });
        }
      };

      const openContactModal = (contact = null, defaultSiteId = null) => {
        const modalId = uiStore.addModal(CustomerContactModal, {
          title: contact
            ? $t('customer_accounts.contacts.edit')
            : $t('customer_accounts.contacts.add'),
          size: 'lg',
          contact,
          sites: activeSites.value,
          defaultSiteId,
          onCancel: () => uiStore.closeModal(modalId),
          onSubmit: async (payload) => {
            try {
              if (contact) {
                await customerAccountService.updateContact(props.leadId, contact.id, payload);
              } else {
                await customerAccountService.createContact(props.leadId, payload);
              }
              uiStore.closeModal(modalId);
              await afterChange(
                contact
                  ? 'customer_accounts.contacts.updated'
                  : 'customer_accounts.contacts.created'
              );
            } catch (error) {
              toastStore.addToast({
                type: 'error',
                message: errorMessage(error, 'customer_accounts.save_failed'),
              });
            }
          },
        });
      };

      const archiveContact = (contact) => {
        const modalId = uiStore.addModal(false, {
          title: $t('customer_accounts.contacts.archive_title', { name: contactName(contact) }),
          description: $t('customer_accounts.contacts.archive_description'),
          submitText: $t('customer_accounts.archive'),
          cancelText: $t('common.cancel'),
          onCancel: () => uiStore.closeModal(modalId),
          onSubmit: async () => {
            uiStore.closeModal(modalId);
            try {
              await customerAccountService.archiveContact(props.leadId, contact.id);
              await afterChange('customer_accounts.contacts.archived');
            } catch (error) {
              toastStore.addToast({
                type: 'error',
                message: errorMessage(error, 'customer_accounts.save_failed'),
              });
            }
          },
        });
      };

      const restoreContact = async (contact) => {
        try {
          await customerAccountService.restoreContact(props.leadId, contact.id);
          await afterChange('customer_accounts.contacts.restored');
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: errorMessage(error, 'customer_accounts.save_failed'),
          });
        }
      };

      return {
        sites,
        activeSites,
        loading,
        loaded,
        search,
        showArchived,
        customerContacts,
        contactsBySite,
        filteredSites,
        usage,
        contactName,
        siteTitle,
        formatSiteAddress,
        openSiteModal,
        openBulkModal,
        archiveSite,
        restoreSite,
        openContactModal,
        archiveContact,
        restoreContact,
      };
    },
  });
</script>

<style scoped lang="scss">
  .customer-account {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    &__section {
      @include card;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;

      @include sm-down {
        flex-direction: column;
      }
    }

    &__header-actions {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__count {
      padding: 0 0.5rem;
      border-radius: 9999px;
      background: var(--slate-100);
      font-size: 0.75rem;
      font-weight: 500;
      color: var(--slate-600);
    }

    &__subtitle {
      margin: 0.25rem 0 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
    }

    &__muted {
      font-size: 0.8125rem;
      font-weight: 400;
      color: var(--slate-500);
    }

    &__empty {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-500);
    }

    &__contacts,
    &__sites {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__contact {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;

      &--archived {
        opacity: 0.6;
      }

      @include sm-down {
        flex-wrap: wrap;
      }
    }

    &__contact-main {
      flex: 1;
      min-width: 0;
    }

    &__contact-name {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.875rem;
      font-weight: 500;
      color: var(--slate-900);

      .icon {
        color: var(--amber-500);
      }
    }

    &__contact-meta {
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;
      margin-top: 0.125rem;
      font-size: 0.8125rem;

      a {
        color: var(--slate-600);
        text-decoration: none;

        @include hover {
          color: var(--brand-600);
        }
      }
    }

    &__roles {
      display: flex;
      gap: 0.25rem;
      flex-wrap: wrap;
    }

    &__role {
      padding: 0.0625rem 0.5rem;
      border-radius: 9999px;
      background: var(--brand-50);
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--brand-700);
      white-space: nowrap;
    }

    &__row-actions {
      display: flex;
      gap: 0.125rem;
      margin-left: auto;
      flex-shrink: 0;
    }

    &__toolbar {
      display: flex;
      align-items: center;
      gap: 1rem;
    }

    &__search {
      flex: 1;
      padding: 0.5rem 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;
      font-size: 0.875rem;

      &:focus {
        outline: none;
        border-color: var(--brand-400);
      }
    }

    &__toggle {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
      color: var(--slate-600);
      white-space: nowrap;
      cursor: pointer;
    }

    &__site {
      padding: 0.75rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.5rem;

      &--archived {
        opacity: 0.6;
      }
    }

    &__site-head {
      display: flex;
      align-items: flex-start;
      gap: 0.625rem;
    }

    &__site-icon {
      margin-top: 0.125rem;
      color: var(--slate-400);
      flex-shrink: 0;
    }

    &__site-info {
      flex: 1;
      min-width: 0;
    }

    &__site-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      font-size: 0.875rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__ref {
      padding: 0 0.375rem;
      border-radius: 0.25rem;
      background: var(--slate-100);
      font-size: 0.6875rem;
      font-weight: 500;
      color: var(--slate-600);
    }

    &__usage {
      display: flex;
      gap: 0.75rem;
      flex-wrap: wrap;
      margin-top: 0.25rem;
      font-size: 0.75rem;
      color: var(--slate-500);

      &--open {
        color: var(--orange-600);
        font-weight: 500;
      }
    }

    &__access {
      display: flex;
      align-items: flex-start;
      gap: 0.375rem;
      margin: 0.5rem 0 0 1.625rem;
      padding: 0.375rem 0.5rem;
      border-radius: 0.375rem;
      background: var(--amber-50);
      font-size: 0.75rem;
      color: var(--amber-800);
      white-space: pre-line;
    }

    &__site-contacts {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      margin: 0.5rem 0 0 1.625rem;
    }

    &__site-contact {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
      font-size: 0.8125rem;
      color: var(--slate-700);

      .icon {
        color: var(--slate-400);
      }
    }

    &__site-contact-name {
      font-weight: 500;
    }

    &__add-inline {
      align-self: flex-start;
      padding: 0;
      border: none;
      background: none;
      font-size: 0.8125rem;
      color: var(--brand-600);
      cursor: pointer;

      @include hover {
        text-decoration: underline;
      }
    }
  }
</style>
